import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import styles from "../view/BookDetails.module.css";
import Footer from "../../../common/UI/Footer";
import { Languages, LibraryBig, Check, Heart, BookOpen, Bookmark, ChevronLeft } from "lucide-react";
import ContentNotFound from "../../../common/UI/ContentNotFound";
import Loading from "../../../common/HousGlass Loader/Loading";
import { useEbookBySlug } from "../../../hooks/useEbooksHooks";
import SingleSimilarBooks from "./SimilarSingleEbooks";
import { useCheckContentInPlaylist, useCreatePlaylist } from "../../../hooks/usePlayListHooks";
import { useLikeStatus, useLikesCount, useLikeActions } from "../../../hooks/useLikesHooks"; // New import
import useAuthStore from "../../../store/authStore";
import { useAddContinueListening } from "../../../hooks/useContinueListening";
import { useSubscriptionCheck } from "../../../hooks/useSubscriptionCheck";
import { Helmet } from "react-helmet";

const SingleBookDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [isMobile, setIsMobile] = useState(false);
  const [isAddingToLibrary, setIsAddingToLibrary] = useState(false);
  const [isInLibraryState, setIsInLibraryState] = useState(false);
  const { isAuthenticated } = useAuthStore();
  const { addContinueListening } = useAddContinueListening();
  const { checkSubscriptionBeforePlay } = useSubscriptionCheck();
  
  
  // Check if device is mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    // Initial check
    checkMobile();
    
    // Add event listener for window resize
    window.addEventListener('resize', checkMobile);
    
    // Cleanup
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Fetch series data using the slug from URL params
  const { ebook: series, isLoading, error, refetchEbook } = useEbookBySlug(id);

  // Like hooks
  const { data: likeStatusData } = useLikeStatus('ebook', series?.id);
  const { data: likesCountData } = useLikesCount('ebook', series?.id);
  const { likeItem, unlikeItem, } = useLikeActions();

  // Check if the book is already in a playlist
  const { 
    data: playlistCheck, 
    isLoading: checkingPlaylist,
    refetch: refetchPlaylistCheck 
  } = useCheckContentInPlaylist(series?.id ? "ebook" : null, series?.id);

  // Create playlist mutation
  const { mutate: createPlaylist, isLoading: creatingPlaylist } = useCreatePlaylist();

  // Update local state when playlistCheck changes
  useEffect(() => {
    if (playlistCheck) {
      setIsInLibraryState(playlistCheck.inPlaylist);
    }
  }, [playlistCheck]);

  // Handle adding to library
  const handleAddToLibrary = () => {
    if (!isAuthenticated) {
      navigate('/signin');
      return;
    }

    if (!series?.id || isAddingToLibrary) return;
    
    setIsAddingToLibrary(true);
    
    // Create a new playlist with this book
    createPlaylist({
      item_type: "ebook",
      content_id: series.id,
      title: `${series.title} - Library`,
      description: `Library entry for ${series.title}`
    }, {
      onSuccess: () => {
        // Immediately update the UI state
        setIsInLibraryState(true);
        
        // Then refetch the actual data
        refetchPlaylistCheck();
      },
      onSettled: () => {
        setIsAddingToLibrary(false);
      }
    });
  };

  // Handle like button click with API integration
  const handleLikeClick = () => {
    if (!isAuthenticated) {
      navigate('/signin');
      return;
    }
    
    if (!series?.id) return;

    if (likeStatusData?.liked) {
      unlikeItem({ 
        item_type: 'ebook', 
        item_id: series.id 
      });
    } else {
      likeItem({ 
        item_type: 'ebook', 
        item_id: series.id 
      });
    }
  };
  
  // Loading state
  if (isLoading || creatingPlaylist) {
    return <div className={styles.loading}><Loading/></div>;
  }
  
  // Error state
  if (!series || error) {
    return <ContentNotFound message="Book Not Found" onRefresh={refetchEbook} />;
  }
  



  // Determine which cover image to use based on device
  const coverImage = isMobile
    ? series.cover_image_url_for_mbl
    : series.cover_image_url_for_computer;

  // Use likes data from hooks
  const likesCount = likesCountData?.count || 0;
  const isLiked = likeStatusData?.liked || false;
  const handleReadClick = () => {
    // Check subscription before reading - only for paid content
    if (!checkSubscriptionBeforePlay(series?.paid)) {
      return;
    }

    // Add continue listening record if authenticated
    if (isAuthenticated) {
      addContinueListening({
        item_type: "ebook",
        item_id: series.id,
      });
    }
    
    navigate(`/book/${series.slug}`);
  };
  
  const handleGoBack = () => {
    const language = series?.language?.toLowerCase();
    const path = language === "hindi" ? "/hindi-reading" : language === "english" ? "/reading" : "/garhwali-reading";
    navigate(path);
  };

  return (
    <>
    <Helmet>
      <title>{series.page_title || `${series.title} - SunoKahaniyan`}</title>
      <meta name="description" content={series.meta_description || series.description || "Read this book on SunoKahaniyan"} />
      {series.meta_keywords && (
        <meta name="keywords" content={series.meta_keywords} />
      )}
    </Helmet>
      <div className={styles.main_container}>
        <div className={styles.container}>
          <div
            onClick={handleGoBack}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              cursor: 'pointer',
              color: '#e50914',
              marginBottom: '12px'
            }}
            aria-label={series?.language?.toLowerCase() === "hindi" ? "वापस जाएँ" : "Go back"}
            role="button"
          >
            <ChevronLeft size={22} />
            <span>{series?.language?.toLowerCase() === "hindi" ? "वापस" : "Back"}</span>
          </div>
          <div className={styles.header}>
            <div className={styles.coverWrapper}>
              <img
                src={coverImage}
                alt={series.title}
                className={styles.cover}
              />
            </div>
            <div className={styles.info}>
              <h1 className={styles.title}>
                {series.title}
                {series?.paid && (
                  <span className={styles.paidIndicator}>Premium</span>
                )}
              </h1>
              <p className={styles.author}> {series?.language?.toLowerCase() === "hindi" ? "लेखक: " : "Author: "} <span style={{ cursor:"pointer", color:"red"}}>{series.author}</span> </p>
              <div className={styles.stats}>
                <div className={styles.stat}>
                  <span className={styles.icon}><LibraryBig size={18}/></span>
                  <span>{series.genre_name}</span>
                </div>
                <div className={styles.stat}>
                  <span className={styles.icon}><Languages size={18}/></span>
                  <span>{series.language}</span>
                </div>
                <div
                  className={`${styles.stat} ${styles.likeButton}`}
                  onClick={handleLikeClick}
                >
                  <Heart
                    className={`${styles.heartIcon} ${isLiked ? styles.liked : ''}`}
                    size={18}
                    fill={isLiked ? "#e50914" : "none"}
                    stroke={isLiked ? "#e50914" : "currentColor"}
                  />
                  <span>{likesCount}</span>
                </div>
              </div>
               <div className={styles.buttonGroup}>
                <button
                  className={styles.readButton}
                  onClick={handleReadClick}
                >
                  <BookOpen size={18} />
                  {series?.language?.toLowerCase() === "hindi" ? "अभी पढ़ें" : "Read Now"}
                </button>
                
                <button
                  className={`${styles.addLibrarybutton} ${
                    isInLibraryState ? styles.inLibrary : ""
                  }`}
                  onClick={handleAddToLibrary}
                  disabled={
                    isInLibraryState || isAddingToLibrary || checkingPlaylist
                  }
                >
                  {isAddingToLibrary ? (
                    series?.language?.toLowerCase() === "hindi" ? "लोड हो रहा है..." : "Loading..."
                  ) : checkingPlaylist ? (
                    series?.language?.toLowerCase() === "hindi" ? "जांच हो रही है..." : "Checking..."
                  ) : isInLibraryState ? (
                    <>
                      <Check size={18} />
                      {series?.language?.toLowerCase() === "hindi" ? "जोड़ा गया" : "Added"}
                    </>
                  ) : (
                    <>
                      <Bookmark size={18} />
                      {series?.language?.toLowerCase() === "hindi" ? "लाइब्रेरी में जोड़ें" : "Add to Library"}
                    </>
                  )}
                </button>
              </div>
              {series.description && (
                <div className={styles.description}>
                  <h3>{series?.language?.toLowerCase() === "hindi" ? "विवरण" : "Description"}</h3>
                  <p>{series.description}</p>
                </div>
              )}
            </div>
          </div>
        </div>
        <SingleSimilarBooks id={series?.id}/>
      </div>
      <Footer />
    </>
  );
};

export default SingleBookDetails;